import { Icon } from '@amelio/ui-library';

import { OBJECTIVES_OVERVIEW_SCALE } from './objectivesOverviewDensity';

import type { ObjectivesOverviewDensity, ObjectivesTilesScale } from './objectivesOverviewDensity';
import type { ObjectivesSummary } from '@/utils/objectivesSummary';
import type { ComponentProps } from 'react';

import { useAmelioTranslations } from '@/hooks';
import { UNKNOWN_FIGURE } from '@/utils/objectivesSummary';
import { OBJECTIVE_STATUS_LABEL_KEYS } from '@/utils/objectiveStatus';

/** Each tile speaks in one colour, for its icon, its figure and its caption alike. */
type MetricTone = 'accent' | 'primary' | 'warning' | 'error' | 'navy';

const TONE_TEXT: Record<MetricTone, string> = {
    accent: 'text-obj-accent',
    primary: 'text-primary',
    warning: 'text-warning',
    error: 'text-error',
    navy: 'text-navy',
};

/** The icon sits on a soft tint of the same colour. */
const TONE_TINT: Record<MetricTone, string> = {
    accent: 'bg-obj-vision-tint',
    primary: 'bg-obj-tint',
    warning: 'bg-obj-tint',
    error: 'bg-obj-tint',
    navy: 'bg-obj-tint',
};

/** The library exports no name type for its icons; the prop of `Icon` is the source of truth. */
type IconName = ComponentProps<typeof Icon>['name'];

interface MetricTileProps {
    title: string;
    icon: IconName;
    value: number;
    tone: MetricTone;
    /** While true the tile shows a dash: the count is not known, and it is not zero either. */
    isLoading?: boolean;
    scale: ObjectivesTilesScale;
    dataTestId: string;
}

/**
 * One headline figure of the tab: a title, its icon and the number. No qualifier under the figure:
 * the period the count covers is named by the picker at the top of the tab.
 */
const MetricTile = ({
    title,
    icon,
    value,
    tone,
    isLoading = false,
    scale,
    dataTestId,
}: MetricTileProps) => (
    <div
        className={`flex flex-col justify-between border border-white bg-white/45 backdrop-blur-[2px] ${scale.tile}`}
        data-testid={dataTestId}
    >
        {/* The icon follows the title instead of being pushed to the far edge: the title shrinks
            and wraps to a second line when it is long, and the icon stays beside it either way.
            `min-w-0` is what lets the heading shrink rather than push the icon out. */}
        <div className={'flex items-start gap-3'}>
            <h4 className={`text-navy min-w-0 font-medium ${scale.title}`}>{title}</h4>
            <span
                className={`${TONE_TINT[tone]} ${scale.iconBox} flex shrink-0 items-center justify-center rounded-md`}
            >
                <Icon
                    name={icon}
                    size={scale.iconSize}
                    colorClassName={TONE_TEXT[tone]}
                    ariaHidden
                />
            </span>
        </div>

        <span
            className={`${TONE_TEXT[tone]} ${scale.value} leading-none font-medium tabular-nums`}
            data-testid={`${dataTestId}-value`}
        >
            {isLoading ? UNKNOWN_FIGURE : value}
        </span>
    </div>
);

interface ObjectivesMetricTilesProps {
    summary: ObjectivesSummary;
    /** Whether the objectives or the periods are still on their way. */
    isLoading?: boolean;
    /** How tightly the row is drawn; `default` is the scale the profile's Objectives tab reads. */
    density?: ObjectivesOverviewDensity;
    dataTestId?: string;
}

/**
 * The headline tiles of the Objectives tab -- the total, then one count per state an objective can
 * read as -- for the objectives inside the selected period. They stack on a phone and sit five
 * abreast on a wide screen; nothing scrolls sideways.
 */
export const ObjectivesMetricTiles = ({
    summary,
    isLoading = false,
    density = 'default',
    dataTestId = 'objectives-metric-tiles',
}: ObjectivesMetricTilesProps) => {
    const { t } = useAmelioTranslations();
    const scale = OBJECTIVES_OVERVIEW_SCALE[density].tiles;

    return (
        // Five abreast only from `xl`: below it a five-way split leaves each title too narrow to
        // read. Grid rows stretch, so a title that wraps to two lines lifts the whole row together.
        <div
            className={`grid sm:grid-cols-2 xl:grid-cols-5 ${scale.grid}`}
            data-testid={dataTestId}
        >
            <MetricTile
                title={t('performanceProfile.objectives.metrics.total')}
                icon={'cryptocompany-target'}
                value={summary.total}
                tone={'accent'}
                isLoading={isLoading}
                scale={scale}
                dataTestId={`${dataTestId}-total`}
            />
            <MetricTile
                title={t('performanceProfile.objectives.metrics.achieved')}
                icon={'essential-tick-circle'}
                value={summary.achieved}
                tone={'primary'}
                isLoading={isLoading}
                scale={scale}
                dataTestId={`${dataTestId}-achieved`}
            />
            <MetricTile
                title={t(OBJECTIVE_STATUS_LABEL_KEYS.onTrack)}
                icon={'business-trend-up'}
                value={summary.onTrack}
                tone={'navy'}
                isLoading={isLoading}
                scale={scale}
                dataTestId={`${dataTestId}-on-track`}
            />
            <MetricTile
                title={t(OBJECTIVE_STATUS_LABEL_KEYS.atRisk)}
                icon={'essential-warning-2'}
                value={summary.atRisk}
                tone={'warning'}
                isLoading={isLoading}
                scale={scale}
                dataTestId={`${dataTestId}-at-risk`}
            />
            <MetricTile
                title={t('performanceProfile.objectives.metrics.overdue')}
                icon={'essential-danger'}
                value={summary.overdue}
                tone={'error'}
                isLoading={isLoading}
                scale={scale}
                dataTestId={`${dataTestId}-overdue`}
            />
        </div>
    );
};
